import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Eye, EyeOff, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput, TextArea } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";
import { ICON_NAMES } from "@/lib/panelIcons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PANEL_KEYS = ["banking", "rewards", "automated", "wealth", "institutional", "security"];

export default function Sections() {
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "sections");
  const [sections, setSections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ heading: "", subheading: "", stats: [], items: [] });

  const load = () => api.get("/sections").then((r) => setSections(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    try { await api.put(`/sections/${s.id}`, { enabled: !s.enabled }); load(); toast.success(`${s.heading} ${!s.enabled ? "shown" : "hidden"}`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const arr = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSections(arr);
    try { await api.post("/sections/reorder", arr.map((s) => s.id)); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); load(); }
  };

  const openEdit = (s) => {
    setForm({
      heading: s.heading || "",
      subheading: s.subheading || "",
      stats: (s.config && s.config.stats) || [],
      items: (s.config && Array.isArray(s.config.items)) ? s.config.items.map((it) => ({ icon: it.icon || "layers", title: it.title || "", desc: it.desc || "", link: it.link || "" })) : [],
    });
    setEditing(s);
  };

  // item helpers
  const updItem = (i, field, val) => setForm((f) => { const items = [...f.items]; items[i] = { ...items[i], [field]: val }; return { ...f, items }; });
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { icon: "layers", title: "", desc: "", link: "" }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }));
  const moveItem = (i, dir) => setForm((f) => { const items = [...f.items]; const j = i + dir; if (j < 0 || j >= items.length) return f; [items[i], items[j]] = [items[j], items[i]]; return { ...f, items }; });

  const save = async () => {
    try {
      const payload = { heading: form.heading, subheading: form.subheading };
      if (editing.key === "stats") payload.config = { stats: form.stats.filter((s) => s.value || s.label) };
      if (PANEL_KEYS.includes(editing.key)) payload.config = { items: form.items.filter((it) => it.title) };
      await api.put(`/sections/${editing.id}`, payload);
      toast.success("Section updated"); setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title="Page & Section Builder" subtitle="Toggle visibility, reorder, and edit each homepage section." />
      <Panel>
        <div className="divide-y divide-[#203E70]">
          {sections.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-3 p-4" data-testid={`section-row-${s.key}`}>
              <div className="flex flex-col">
                <button disabled={!canEdit || idx === 0} onClick={() => move(idx, -1)} className="text-slate-500 hover:text-[#D8AE4E] disabled:opacity-30" data-testid={`section-up-${s.key}`}><ChevronUp size={16} /></button>
                <button disabled={!canEdit || idx === sections.length - 1} onClick={() => move(idx, 1)} className="text-slate-500 hover:text-[#D8AE4E] disabled:opacity-30" data-testid={`section-down-${s.key}`}><ChevronDown size={16} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${s.enabled ? "text-white" : "text-slate-500"}`}>{s.heading}</span>
                  <code className="text-xs text-slate-600 font-mono">{s.key}</code>
                </div>
                {s.subheading && <p className="text-xs text-slate-500 mt-0.5 truncate">{s.subheading}</p>}
              </div>
              {!s.enabled && <span className="text-xs text-slate-500 shrink-0">Hidden</span>}
              <div className="flex items-center gap-1 shrink-0">
                {canEdit && <button onClick={() => toggle(s)} className={`p-2 ${s.enabled ? "text-[#D8AE4E]" : "text-slate-500"} hover:opacity-80`} data-testid={`section-toggle-${s.key}`}>{s.enabled ? <Eye size={16} /> : <EyeOff size={16} />}</button>}
                {canEdit && <button onClick={() => openEdit(s)} className="p-2 text-slate-400 hover:text-[#D8AE4E]" data-testid={`section-edit-${s.key}`}><Pencil size={15} /></button>}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0B1D40] border-[#203E70] text-white max-h-[85vh] overflow-y-auto admin-scroll">
          <DialogHeader><DialogTitle className="font-serif text-xl">Edit “{editing?.key}” section</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <TextInput label="Heading" value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} testid="section-form-heading" />
            <TextArea label="Subheading" rows={2} value={form.subheading} onChange={(e) => setForm({ ...form, subheading: e.target.value })} testid="section-form-sub" />
            {editing?.key === "stats" && (
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Stats (only verified figures — leave empty to hide section)</label>
                <div className="space-y-2">
                  {form.stats.map((st, i) => (
                    <div key={i} className="flex gap-2">
                      <input placeholder="Value e.g. $4.2B+" value={st.value} onChange={(e) => { const s = [...form.stats]; s[i] = { ...s[i], value: e.target.value }; setForm({ ...form, stats: s }); }} className="flex-1 bg-[#06122E] border border-[#203E70] rounded px-3 py-2 text-sm text-white" data-testid={`stat-value-${i}`} />
                      <input placeholder="Label" value={st.label} onChange={(e) => { const s = [...form.stats]; s[i] = { ...s[i], label: e.target.value }; setForm({ ...form, stats: s }); }} className="flex-1 bg-[#06122E] border border-[#203E70] rounded px-3 py-2 text-sm text-white" data-testid={`stat-label-${i}`} />
                      <button onClick={() => setForm({ ...form, stats: form.stats.filter((_, x) => x !== i) })} className="text-red-400 px-2">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setForm({ ...form, stats: [...form.stats, { value: "", label: "" }] })} className="btn-outline-gold px-3 py-1.5 text-xs" data-testid="add-stat">+ Add stat</button>
                </div>
              </div>
            )}
            {PANEL_KEYS.includes(editing?.key) && (
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Panel items — icon, title, description &amp; link</label>
                <div className="space-y-3">
                  {form.items.map((it, i) => (
                    <div key={i} className="border border-[#203E70] rounded p-3 space-y-2" data-testid={`item-row-${i}`}>
                      <div className="flex gap-2 items-start">
                        <select value={it.icon || "layers"} onChange={(e) => updItem(i, "icon", e.target.value)} className="bg-[#06122E] border border-[#203E70] rounded px-2 py-2 text-sm text-white" data-testid={`item-icon-${i}`}>
                          {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <input placeholder="Title" value={it.title || ""} onChange={(e) => updItem(i, "title", e.target.value)} className="flex-1 bg-[#06122E] border border-[#203E70] rounded px-3 py-2 text-sm text-white" data-testid={`item-title-${i}`} />
                        <div className="flex flex-col">
                          <button onClick={() => moveItem(i, -1)} className="text-slate-500 hover:text-[#D8AE4E] disabled:opacity-30" disabled={i === 0}><ChevronUp size={14} /></button>
                          <button onClick={() => moveItem(i, 1)} className="text-slate-500 hover:text-[#D8AE4E] disabled:opacity-30" disabled={i === form.items.length - 1}><ChevronDown size={14} /></button>
                        </div>
                        <button onClick={() => removeItem(i)} className="text-red-400 px-2 py-2" data-testid={`item-remove-${i}`}>✕</button>
                      </div>
                      <input placeholder="Description" value={it.desc || ""} onChange={(e) => updItem(i, "desc", e.target.value)} className="w-full bg-[#06122E] border border-[#203E70] rounded px-3 py-2 text-sm text-white" data-testid={`item-desc-${i}`} />
                      <input placeholder="Link — internal path (/markets) or Link Manager key (live_register)" value={it.link || ""} onChange={(e) => updItem(i, "link", e.target.value)} className="w-full bg-[#06122E] border border-[#203E70] rounded px-3 py-2 text-sm text-white font-mono" data-testid={`item-link-${i}`} />
                    </div>
                  ))}
                  <button onClick={addItem} className="btn-outline-gold px-3 py-1.5 text-xs" data-testid="add-item">+ Add item</button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold px-4 py-2 text-sm">Cancel</button>
            <button type="button" onClick={save} className="btn-gold px-4 py-2 text-sm" data-testid="section-form-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
